import React, { useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { Card, Row, Col, Select, DatePicker, Statistic, Space } from 'antd';
import { RiseOutlined, PieChartOutlined, BarChartOutlined } from '@ant-design/icons';
import { formatCurrency } from '../../../../shared/utils/formatters';
import { CHART_COLORS } from '../../../../config/constants';

import AdvancedCharts from '../AdvancedCharts';
import dayjs from 'dayjs';
import styles from './Charts.module.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const { Option } = Select;
const { RangePicker } = DatePicker;

export default function Charts({ debts, payments }) {
  const [chartType, setChartType] = useState('monthly');
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(6, 'month'),
    dayjs()
  ]);

  // Dữ liệu cho biểu đồ xu hướng theo tháng
  const getMonthlyTrendData = () => {
    const months = [];
    const debtData = [];
    const paymentData = [];

    for (let i = 5; i >= 0; i--) {
      const month = dayjs().subtract(i, 'month');
      months.push(month.format('MM/YYYY'));

      const monthlyDebts = debts.filter(debt =>
        dayjs(debt.ngayVay).format('YYYY-MM') === month.format('YYYY-MM')
      );

      const monthlyPayments = payments.filter(payment =>
        dayjs(payment.ngayTra).format('YYYY-MM') === month.format('YYYY-MM')
      );

      debtData.push(monthlyDebts.reduce((sum, debt) => sum + debt.soTien, 0));
      paymentData.push(monthlyPayments.reduce((sum, payment) => sum + payment.soTienTra, 0));
    }

    return {
      labels: months,
      datasets: [
        {
          label: 'Nợ mới',
          data: debtData,
          borderColor: CHART_COLORS.ERROR,
          backgroundColor: CHART_COLORS.ERROR.replace('0.8', '0.2'),
          tension: 0.4,
          fill: false,
        },
        {
          label: 'Đã trả',
          data: paymentData,
          borderColor: CHART_COLORS.SUCCESS,
          backgroundColor: CHART_COLORS.SUCCESS.replace('0.8', '0.2'),
          tension: 0.4,
          fill: false,
        },
      ],
    };
  };

  // Dữ liệu cho biểu đồ phân bố theo người vay
  const getLenderDistributionData = () => {
    const lenderStats = {};

    debts.forEach(debt => {
      if (!lenderStats[debt.nguoiVay]) {
        lenderStats[debt.nguoiVay] = {
          totalDebt: 0,
          totalPaid: 0,
          count: 0
        };
      }
      lenderStats[debt.nguoiVay].totalDebt += debt.soTien;
      lenderStats[debt.nguoiVay].totalPaid += (debt.soTienDaTra || 0);
      lenderStats[debt.nguoiVay].count += 1;
    });

    const labels = Object.keys(lenderStats);
    const totalDebtData = labels.map(lender => lenderStats[lender].totalDebt);
    const remainingData = labels.map(lender =>
      lenderStats[lender].totalDebt - lenderStats[lender].totalPaid
    );

    return {
      labels,
      datasets: [
        {
          label: 'Tổng nợ (VNĐ)',
          data: totalDebtData,
          backgroundColor: CHART_COLORS.PRIMARY,
          borderColor: CHART_COLORS.PRIMARY.replace('0.8', '1'),
          borderWidth: 1,
        },
        {
          label: 'Còn nợ (VNĐ)',
          data: remainingData,
          backgroundColor: CHART_COLORS.ERROR,
          borderColor: CHART_COLORS.ERROR.replace('0.8', '1'),
          borderWidth: 1,
        },
      ],
    };
  };

  // Biểu đồ trạng thái nợ
  const getDebtStatusData = () => {
    const fullyPaid = debts.filter(debt => debt.daTra).length;
    const partiallyPaid = debts.filter(debt =>
      !debt.daTra && (debt.soTienDaTra || 0) > 0
    ).length;
    const unpaid = debts.filter(debt =>
      !debt.daTra && (debt.soTienDaTra || 0) === 0
    ).length;
    const overdue = debts.filter(debt =>
      !debt.daTra && dayjs(debt.ngayTra).isBefore(dayjs(), 'day')
    ).length;

    return {
      labels: ['Đã trả hết', 'Trả một phần', 'Chưa trả', 'Quá hạn'],
      datasets: [
        {
          data: [fullyPaid, partiallyPaid, unpaid, overdue],
          backgroundColor: [
            CHART_COLORS.SUCCESS,
            CHART_COLORS.WARNING,
            CHART_COLORS.INFO,
            CHART_COLORS.ERROR,
          ],
          borderColor: [
            CHART_COLORS.SUCCESS.replace('0.8', '1'),
            CHART_COLORS.WARNING.replace('0.8', '1'),
            CHART_COLORS.INFO.replace('0.8', '1'),
            CHART_COLORS.ERROR.replace('0.8', '1'),
          ],
          borderWidth: 2,
        },
      ],
    };
  };

  // Biểu đồ thanh toán theo tháng
  const getPaymentTrendData = () => {
    const months = [];
    const paymentData = [];

    for (let i = 11; i >= 0; i--) {
      const month = dayjs().subtract(i, 'month');
      months.push(month.format('MM/YY'));

      const monthlyPayments = payments.filter(payment =>
        dayjs(payment.ngayTra).format('YYYY-MM') === month.format('YYYY-MM')
      );

      paymentData.push(monthlyPayments.reduce((sum, payment) => sum + payment.soTienTra, 0));
    }

    return {
      labels: months,
      datasets: [
        {
          label: 'Số tiền đã trả',
          data: paymentData,
          backgroundColor: CHART_COLORS.SUCCESS,
          borderColor: CHART_COLORS.SUCCESS.replace('0.8', '1'),
          borderWidth: 1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            if (context.dataset.label?.includes('VNĐ')) {
              return `${context.dataset.label}: ${formatCurrency(context.parsed.y || context.parsed)}`;
            }
            return `${context.dataset.label}: ${context.parsed.y || context.parsed}`;
          }
        }
      }
    },
    scales: chartType !== 'status' ? {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return formatCurrency(value);
          }
        }
      }
    } : undefined
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `${context.label}: ${context.parsed} khoản`;
          }
        }
      }
    }
  };

  // Tính toán thống kê tổng quan
  const calculateOverallStats = () => {
    const totalDebt = debts.reduce((sum, debt) => sum + debt.soTien, 0);
    const totalPaid = payments.reduce((sum, payment) => sum + payment.soTienTra, 0);
    const avgPayment = payments.length > 0 ? totalPaid / payments.length : 0;
    const avgDebt = debts.length > 0 ? totalDebt / debts.length : 0;

    return { totalDebt, totalPaid, avgPayment, avgDebt };
  };

  const overallStats = calculateOverallStats();

  return (
    <div className={styles.chartsContainer}>
      {/* Thống kê tổng quan */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng nợ"
              value={overallStats.totalDebt}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Tổng đã trả"
              value={overallStats.totalPaid}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Trung bình mỗi lần trả"
              value={overallStats.avgPayment}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Trung bình mỗi khoản nợ"
              value={overallStats.avgDebt}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Bộ điều khiển */}
      <Card className={styles.controlCard}>
        <Space size="middle" wrap>
          <div>
            <label>Loại biểu đồ: </label>
            <Select
              value={chartType}
              onChange={setChartType}
              style={{ width: 200 }}
            >
              <Option value="monthly">Xu hướng theo tháng</Option>
              <Option value="lender">Phân bố theo người vay</Option>
              <Option value="status">Trạng thái nợ</Option>
              <Option value="payment">Thanh toán theo tháng</Option>
            </Select>
          </div>

          <div>
            <label>Khoảng thời gian: </label>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="MM/YYYY"
              picker="month"
            />
          </div>
        </Space>
      </Card>

      {/* Biểu đồ chính */}
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                {chartType === 'monthly' && <RiseOutlined />}
                {chartType === 'lender' && <BarChartOutlined />}
                {chartType === 'status' && <PieChartOutlined />}
                {chartType === 'payment' && <BarChartOutlined />}
                Biểu đồ chính
              </Space>
            }
            className={styles.chartCard}
          >
            <div className={styles.chartContainer}>
              {chartType === 'monthly' && (
                <Line data={getMonthlyTrendData()} options={chartOptions} />
              )}
              {chartType === 'lender' && (
                <Bar data={getLenderDistributionData()} options={chartOptions} />
              )}
              {chartType === 'status' && (
                <Doughnut data={getDebtStatusData()} options={pieOptions} />
              )}
              {chartType === 'payment' && (
                <Bar data={getPaymentTrendData()} options={chartOptions} />
              )}
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card title="🥧 Trạng thái tổng quan" className={styles.chartCard}>
            <div className={styles.pieContainer}>
              <Pie data={getDebtStatusData()} options={pieOptions} />
            </div>
          </Card>
        </Col>
      </Row>

      <AdvancedCharts debts={debts} payments={payments} />
    </div>
  );
}
